import type { ChipVariantProps } from './Chip.variants';

type Size = NonNullable<ChipVariantProps['size']>;
type Tint = NonNullable<ChipVariantProps['tint']>;

/**
 * Icon size per chip size — applies to both the leading icon and the
 * trailing circle-x remove control. Values come from the Figma per-size
 * bands (the "Plant" / "circle-x" layers): XL 22, L 20, M 18, S 16.
 */
export const ICON_SIZE_FOR: Record<Size, number> = {
  xl: 22,
  l: 20,
  m: 18,
  s: 16,
};

/**
 * Resolved icon color per (tint, dark-mode). Mirrors `chipTextVariants`. We
 * resolve to an rgb string here because `lucide-react-native` /
 * `react-native-svg` accept `color` as a prop, not a className. On web the
 * icon instead inherits `currentColor` from the Pressable's text color (see
 * `Chip.tsx`), so this map is only consulted on native.
 *
 * The icon must always match its label text. `chipTextVariants` colors the
 * label with `text-{tint}-600`, and the shared palette inverts every shade in
 * dark mode as `{shade} → {1000 − shade}` (generate-css-vars.cjs). So the icon
 * hex is the light `-600` and, in dark, the mirror `-400` (= what `-600`
 * resolves to under inversion) — NOT the `-300` this map used to guess before
 * the dark spec existed. Verified against Figma's dark frame (node 2787:31842):
 * every selected tint's icon/text is `{tint}/600` inverted, e.g. New → #51a2ff,
 * Review → #ff8904.
 */
const ICON_COLOR: Record<Tint, { light: string; dark: string }> = {
  // secondary-600 (#525252) / dark secondary-400 (#a1a1a1)
  neutral: { light: 'rgb(82, 82, 82)', dark: 'rgb(161, 161, 161)' },
  // tertiary-600 (#155dfc) / dark tertiary-400 (#51a2ff)
  new: { light: 'rgb(21, 93, 252)', dark: 'rgb(81, 162, 255)' },
  // orange-600 (#f54900) / dark orange-400 (#ff8904)
  review: { light: 'rgb(245, 73, 0)', dark: 'rgb(255, 137, 4)' },
  // green-600 (#00a63e) / dark green-400 (#05df72)
  memorised: { light: 'rgb(0, 166, 62)', dark: 'rgb(5, 223, 114)' },
  // amber-600 (#e17100) / dark amber-400 (#ffb900)
  toReview: { light: 'rgb(225, 113, 0)', dark: 'rgb(255, 185, 0)' },
  // amber-600 (#e17100) / dark amber-400 (#ffb900) — shares the amber ramp with
  // `toReview` (favourite tint; see Chip.variants.ts).
  starred: { light: 'rgb(225, 113, 0)', dark: 'rgb(255, 185, 0)' },
  // primary-600 (#7f22fe) / dark primary-400 (#a684ff)
  primary: { light: 'rgb(127, 34, 254)', dark: 'rgb(166, 132, 255)' },
  // pink-600 (#e60076) / dark pink-400 (#fb64b6)
  pink: { light: 'rgb(230, 0, 118)', dark: 'rgb(251, 100, 182)' },
  // rose-600 (#ec003f) / dark rose-400 (#ff637e)
  rose: { light: 'rgb(236, 0, 63)', dark: 'rgb(255, 99, 126)' },
  // lime-600 (#5ea500) / dark lime-400 (#9ae600)
  lime: { light: 'rgb(94, 165, 0)', dark: 'rgb(154, 230, 0)' },
};

export function resolveChipIconColor(tint: Tint, isDark: boolean): string {
  const pair = ICON_COLOR[tint];
  return isDark ? pair.dark : pair.light;
}
